import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  AlertBanner,
  type AlertBannerColorTheme,
  Box,
  Button,
  Typography,
} from '@saviynt/design-system';

import { usePostCustomListMutation } from '../../../features/ChatModule';
import {
  ALERT_BANNER_DEFAULT,
  CF_SEC_BLD,
  GLOBAL_ALERT_BANNER_TYPES,
  LIMIT_INLINE_COUNT,
} from '../../constants';
import { type ActionBlockOption, type EndpointBlock } from '../../types';
import UiWrapper, { WRAPPER_MODE } from '../UiWrapper';

import FormAlertBanner from './FormAlertBanner';
import FormBody from './FormBody';
import { FormProvider } from './hooks';
import {
  type FormData,
  type FormFieldState,
  type TrackedFormData,
} from './types';
import {
  addTracker,
  convertNestedToLinear,
  countFields,
  getFilledForm,
  processTracker,
  removeTracker,
  sanitizeFields,
  validateForm,
} from './utils';

import './Form.css';

interface FormProps {
  id: string;
  title?: string;
  isLast?: boolean;
  isFullView?: boolean;
  isViewing?: boolean;
  isRightWindowOpen?: boolean;
  isProcessing?: boolean;
  isChanged?: boolean;
  resetKey?: string;
  workflowType: string;
  data: FormData[];
  actions: ActionBlockOption[];
  retentions?: FormFieldState[];
  onExpand?: (customFormData?: FormFieldState[]) => void;
  onInlineExpand?: () => void;
  onCollapse?: () => void;
  onSubmit?: (formData: FormData[]) => void;
}

/**
 * Form
 */
function Form({
  id,
  title,
  isLast,
  isViewing,
  isFullView,
  isRightWindowOpen,
  isProcessing,
  isChanged,
  resetKey,
  workflowType,
  data,
  actions,
  retentions,
  onInlineExpand,
  onExpand,
  onCollapse,
  onSubmit,
}: FormProps) {
  const [currentMode, setCurrenMode] = useState(
    isFullView ? WRAPPER_MODE.EXPANDED : WRAPPER_MODE.INLINE
  );
  const [formData, setFormData] = useState<TrackedFormData[]>([]);
  const [formAction, setFormAction] = useState<ActionBlockOption[]>([]);
  const [alertBanner, setAlertBanner] = useState(ALERT_BANNER_DEFAULT);
  const [inlineItemCount, setInlineItemCount] = useState<number | null>(null);

  // Refs
  const hasExpandedRef = useRef(false);

  const [fetchValidations, { isLoading: isValidationLoading }] =
    usePostCustomListMutation();

  const handleValidations = useCallback(
    async ({
      url,
      validationData,
      queryParams,
    }: {
      url: string;
      validationData: object;
      queryParams?: object | undefined;
    }) => {
      try {
        const controller = fetchValidations({
          url,
          data: validationData,
          queryParams,
        });
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const typedError = error as {
          data?: {
            response?: { data?: { error: string; msg: string } };
            message: string;
          };
        };
        const errorMessage =
          typedError.data?.response?.data?.error ??
          typedError.data?.message ??
          'Something went wrong';

        throw Error(errorMessage);
      }
    },
    [fetchValidations]
  );

  // Constants
  const formTitle = useMemo(() => {
    if (Array.isArray(data) && data.length > 0) {
      return `Specify ${data[0].title}`
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return 'Specify Details';
  }, [data]);

  // initialization
  useEffect(() => {
    const modifiedFormData = addTracker(data);
    const fieldCount = countFields(modifiedFormData);

    if (retentions) {
      setFormData(getFilledForm(modifiedFormData, retentions));
    } else {
      setFormData(modifiedFormData);
    }

    if (fieldCount > LIMIT_INLINE_COUNT) {
      setInlineItemCount(1);
    }
  }, [data, retentions]);

  useEffect(() => {
    setFormAction(actions);
  }, [actions]);

  // Utils
  useEffect(() => {
    if (isFullView) {
      setCurrenMode(WRAPPER_MODE.EXPANDED);

      return;
    }

    if (isViewing) {
      setCurrenMode(WRAPPER_MODE.VIEWING);

      return;
    }

    if (isLast) {
      setCurrenMode(WRAPPER_MODE.INLINE);

      return;
    }

    setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [isFullView, isLast, isViewing, isChanged, resetKey]);

  useEffect(() => {
    if (inlineItemCount !== null && onExpand && !hasExpandedRef.current) {
      setCurrenMode(WRAPPER_MODE.VIEWING);
      setInlineItemCount(null);
      hasExpandedRef.current = true;

      onExpand([]);
    }
  }, [inlineItemCount, onExpand]);

  const handleWrapperExpand = useCallback(() => {
    if (currentMode === WRAPPER_MODE.INLINE_COLLAPSED) {
      setCurrenMode(WRAPPER_MODE.INLINE);

      onInlineExpand?.();

      return;
    }

    if (currentMode === WRAPPER_MODE.INLINE && onExpand) {
      setCurrenMode(WRAPPER_MODE.VIEWING);

      const untrackedForm = removeTracker(formData);
      const linearizedForm = convertNestedToLinear(untrackedForm);
      const sanitizedForm = sanitizeFields(linearizedForm);

      onExpand(sanitizedForm);

      return;
    }

    setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
  }, [currentMode, formData, onExpand, onInlineExpand]);

  const handleSubmit = useCallback(() => {
    const isVerified = validateForm(formData);

    if (!isVerified) {
      setFormData((prev) => processTracker(prev));

      return;
    }

    if (onSubmit) {
      const untrackedForm = removeTracker(formData);
      const linearizedForm = convertNestedToLinear(untrackedForm);
      const sanitizedForm = sanitizeFields(linearizedForm);

      // No validation exist for custom form
      const firstAction = formAction[0] as ActionBlockOption & {
        validationEndpoint?: EndpointBlock;
      };

      if (formAction.length === 0 || !firstAction.validationEndpoint) {
        if (isFullView && currentMode === WRAPPER_MODE.EXPANDED && onCollapse) {
          onCollapse();
        }

        onSubmit(sanitizedForm as unknown as FormData[]);

        return;
      }

      const currentAction = formAction[0] as ActionBlockOption & {
        validationEndpoint: EndpointBlock;
      };

      const wantedFields = currentAction.validationEndpoint.payload as {
        fields: {
          validationType: string;
          validateValue: string;
        }[];
      };
      const fields = wantedFields.fields.map((field) => {
        const { validateValue } = field;
        const match = validateValue.match(/{{\s*([^}]+)\s*}}/);

        if (match) {
          const placeholder = match[1];
          const targetField = sanitizedForm.find(
            (sf) => sf.name === placeholder
          );

          return {
            ...field,
            validateValue: targetField?.value ?? '',
          };
        }

        return field;
      });
      const validatePayload = {
        url: currentAction.validationEndpoint.url,
        validationData: {
          ...currentAction.validationEndpoint.payload,
          fields,
        },
      };

      handleValidations(validatePayload)
        .then((response) => {
          const typedResponse = response as {
            valid: boolean;
            validations: {
              validationType: string;
              valid: boolean;
              errors: string[];
            }[];
          };

          if (!typedResponse.valid) {
            setAlertBanner({
              ...ALERT_BANNER_DEFAULT,
              kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
              title: '',
              description: typedResponse.validations
                .filter((v) => !v.valid)
                .map((v) => v.errors.join(', '))
                .join(', '),
              isOpen: true,
            });

            return;
          }

          if (
            isFullView &&
            currentMode === WRAPPER_MODE.EXPANDED &&
            onCollapse
          ) {
            onCollapse();
          }

          onSubmit(sanitizedForm as unknown as FormData[]);
        })
        .catch((error) => {
          const typedError = error as { message: string };

          setAlertBanner({
            ...ALERT_BANNER_DEFAULT,
            kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
            title: '',
            description: typedError.message,
            isOpen: true,
          });

          Logger.log(typedError.message);
        });
    }
  }, [
    currentMode,
    isFullView,
    formData,
    formAction,
    onCollapse,
    onSubmit,
    handleValidations,
  ]);

  return (
    <FormProvider workflowType={workflowType}>
      <UiWrapper
        id={id}
        isActive={Boolean(isLast)}
        isRightWindowOpen={Boolean(isRightWindowOpen)}
        isEdited={isChanged}
        mode={currentMode}
        title={title ?? formTitle}
        isDisabled={isProcessing}
        onExpand={handleWrapperExpand}
        onCollapse={onCollapse}
        onClose={() => {
          setCurrenMode(WRAPPER_MODE.INLINE_COLLAPSED);
        }}
        footer={
          Array.isArray(actions) && actions.length > 0 ? (
            <Box tag='div' className='Form-footerWrapper'>
              <Button
                kind='outlined'
                onClick={handleSubmit}
                isDisabled={isValidationLoading || isProcessing}
                isLoading={isValidationLoading}
                className='ListViewer-footerActionButton'
                dataTestId='form-continue-button'>
                <FormattedMessage
                  id='8jETK0'
                  defaultMessage='Continue'
                  description='Continue'
                />
              </Button>
            </Box>
          ) : null
        }
        className='Form'>
        <FormAlertBanner />
        <AlertBanner
          colorTheme={alertBanner.kind as AlertBannerColorTheme}
          title={alertBanner.title}
          description={alertBanner.description}
          shouldAutoDismiss
          isColonVisible={false}
          isVisible={alertBanner.isOpen}
          dataTestId='savi-copilot-custom-form-alert-banner'
          className='FormAlertBanner'
          onCancel={() => {
            setAlertBanner({
              ...ALERT_BANNER_DEFAULT,
              kind: alertBanner.kind,
              isOpen: false,
            });
          }}
        />
        {isFullView && (
          <Box tag='div'>
            <Typography kind='h2' color={CF_SEC_BLD}>
              {title ?? formTitle}
            </Typography>
          </Box>
        )}
        <FormBody formData={formData} setFormData={setFormData} />
      </UiWrapper>
    </FormProvider>
  );
}

Form.defaultProps = {
  title: null,
  isLast: true,
  isFullView: false,
  isRightWindowOpen: false,
  isProcessing: false,
  isViewing: false,
  isChanged: false,
  resetKey: '',
  retentions: null,
  onInlineExpand: undefined,
  onExpand: undefined,
  onCollapse: undefined,
  onSubmit: undefined,
};

export default memo(Form);
